import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, GraduationCap, Calendar, Sparkles } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { isLocale, type Locale } from '@/client/i18n';

export function Dashboard() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  return (
    <div className="space-y-6">
      {/* Hero empty state */}
      <section className="dr-card-static relative p-8 sm:p-12 lg:p-16 overflow-hidden text-center">
        {/* Orb glow behind mascot */}
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />

        <div className="relative">
          <div className="relative mx-auto h-32 w-32 sm:h-40 sm:w-40">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'conic-gradient(from 220deg, rgba(193,167,226,0.40), rgba(117,59,189,0.18), rgba(193,167,226,0.40))',
                filter: 'blur(8px)',
              }}
              aria-hidden
            />
            <img
              src="/brand/dajana-purple.svg"
              alt=""
              className="relative h-full w-full object-contain animate-float"
              loading="lazy"
            />
          </div>

          <p className="mt-6 dr-eyebrow">
            {t('portal.empty_courses_eyebrow', { defaultValue: 'Začni svoju cestu' })}
          </p>
          <h2 className="mt-3 font-display text-h2 text-primary-dark">
            {t('portal.empty_courses_title')}
          </h2>
          <p className="mt-4 font-display text-body-large text-text-secondary max-w-md mx-auto leading-relaxed">
            {t('portal.empty_courses_body')}
          </p>
          <Link to={`/${locale}/kurzy`} className="inline-block mt-8">
            <Button size="lg">
              {t('portal.empty_courses_cta')}
              <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Suggested actions */}
      <section className="grid gap-5 sm:grid-cols-2">
        <SuggestionCard
          Icon={GraduationCap}
          eyebrow={t('portal.suggest_courses_eyebrow', { defaultValue: 'Kurzy' })}
          title={t('portal.suggest_courses_title', {
            defaultValue: 'Prečítaj si o autorských kurzoch',
          })}
          body={t('portal.suggest_courses_body', {
            defaultValue: 'Praktické lekcie, doživotný prístup, postupné odomykanie epizód.',
          })}
          ctaLabel={t('nav.courses')}
          to={`/${locale}/kurzy`}
        />
        <SuggestionCard
          Icon={Calendar}
          eyebrow={t('portal.suggest_consult_eyebrow', { defaultValue: 'Konzultácia 1:1' })}
          title={t('portal.suggest_consult_title', {
            defaultValue: 'Rezervuj si čas s Dajanou',
          })}
          body={t('portal.suggest_consult_body', {
            defaultValue: 'Hodina osobnej konzultácie ti dá smer a odpovede na tvoju situáciu.',
          })}
          ctaLabel={t('nav.consultations')}
          to={`/${locale}/konzultacie`}
          accent="alt"
        />
      </section>

      {/* Subtle tip */}
      <section className="rounded-2xl bg-white/40 backdrop-blur-md border border-glass-border p-4 sm:p-5 flex items-start gap-3">
        <Sparkles size={16} className="text-primary mt-0.5 shrink-0" />
        <p className="font-sans text-body-small text-text-secondary leading-relaxed">
          {t('portal.tip', {
            defaultValue:
              'Tip: aktívne kurzy a rezervácie sa zobrazia tu vždy, keď máš čo robiť.',
          })}
        </p>
      </section>
    </div>
  );
}

function SuggestionCard({
  Icon,
  eyebrow,
  title,
  body,
  ctaLabel,
  to,
  accent = 'default',
}: {
  Icon: typeof GraduationCap;
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  to: string;
  accent?: 'default' | 'alt';
}) {
  return (
    <Link
      to={to}
      className="group dr-card p-6 sm:p-7 flex flex-col hover:-translate-y-1"
    >
      <div
        className={`absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
          accent === 'alt'
            ? 'bg-gradient-to-br from-[#f7c5dc]/25 via-transparent to-transparent'
            : 'bg-gradient-to-br from-primary/12 via-transparent to-transparent'
        }`}
        aria-hidden
      />
      <div className="relative">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary mb-5 group-hover:scale-110 transition-transform">
          <Icon size={22} />
        </div>
        <p className="dr-eyebrow">{eyebrow}</p>
        <h3 className="mt-2 font-display text-h4 text-primary-dark">{title}</h3>
        <p className="mt-2 font-sans text-body-small text-text-secondary leading-relaxed">
          {body}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary group-hover:gap-2.5 transition-all">
          {ctaLabel}
          <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}
